import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FieldInput } from "@/components/admin/FieldInput";
import { cmsList, cmsCreate, cmsUpdate, cmsDelete, formatApiError } from "@/lib/cms";

export const CrudManager = ({ resource, title, fields, titleKey = "name", subKey, onChanged }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // object being edited/created
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await cmsList(resource));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [resource]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => setEditing({});
  const openEdit = (item) => setEditing({ ...item });

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) await cmsUpdate(resource, editing.id, editing);
      else await cmsCreate(resource, editing);
      toast.success("Saved");
      setEditing(null);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item[titleKey] || "this item"}"?`)) return;
    try {
      await cmsDelete(resource, item.id);
      toast.success("Deleted");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e, "Delete failed"));
    }
  };

  return (
    <div data-testid={`crud-${resource}`}>
      <div className="mb-6 flex items-center justify-between">
        <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">{title}</h2>
        <button
          onClick={openNew}
          data-testid={`add-${resource}-btn`}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-xs font-bold uppercase tracking-wide text-white hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" /> Add New
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <div className="divide-y divide-border border border-border">
          {items.map((item) => (
            <div key={item.id} data-testid={`${resource}-row-${item.id}`} className="flex items-center gap-4 bg-card p-4">
              {(item.image || item.logo) && (
                <img src={item.image || item.logo} alt="" className="h-12 w-12 rounded-lg border border-border bg-white object-contain p-1" />
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate font-bold text-foreground">{item[titleKey] || "(untitled)"}</div>
                {subKey && <div className="truncate text-xs text-muted-foreground">{item[subKey]}</div>}
              </div>
              <button onClick={() => openEdit(item)} data-testid={`edit-${item.id}`} className="rounded-lg border border-border p-2 hover:border-primary hover:text-primary">
                <Pencil className="h-4 w-4" />
              </button>
              <button onClick={() => remove(item)} data-testid={`delete-${item.id}`} className="rounded-lg border border-border p-2 text-destructive hover:border-destructive">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          {items.length === 0 && <div className="bg-card p-8 text-center text-sm text-muted-foreground">No items yet.</div>}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold tracking-tight">
              {editing?.id ? "Edit" : "Add"} {title}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              {fields.map((f) => (
                <div key={f.key}>
                  <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-muted-foreground">
                    {f.label}
                  </label>
                  <FieldInput
                    field={f}
                    value={editing[f.key]}
                    onChange={(v) => setEditing((prev) => ({ ...prev, [f.key]: v }))}
                  />
                </div>
              ))}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={save}
                  disabled={saving}
                  data-testid="crud-save-btn"
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-bold uppercase tracking-wide text-white hover:bg-primary/90 disabled:opacity-60"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                </button>
                <button onClick={() => setEditing(null)} className="flex items-center gap-1 rounded-lg border-2 border-border px-4 py-2.5 text-sm font-bold uppercase tracking-wide">
                  <X className="h-4 w-4" /> Cancel
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
